import { createTheme } from "@mui/material/styles";

import { APP_UI } from "../ui/appUi";

const theme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: APP_UI.accent,
      contrastText: "#0a3139",
    },
    success: { main: APP_UI.success },
    warning: { main: APP_UI.warning },
    error: { main: APP_UI.error },
    background: {
      default: APP_UI.background,
      paper: APP_UI.surfaceSolid,
    },
    text: {
      primary: APP_UI.text,
      secondary: APP_UI.textMuted,
      disabled: "rgba(245,245,247,0.43)",
    },
    divider: APP_UI.border,
    action: {
      active: APP_UI.text,
      hover: APP_UI.cardHover,
      selected: APP_UI.accentSoft,
      disabled: "rgba(245,245,247,0.43)",
      disabledBackground: "rgba(24,27,31,0.94)",
    },
  },

  typography: {
    fontFamily: "Roboto, Arial, sans-serif",
  },

  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          color: APP_UI.text,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          color: APP_UI.text,
          backgroundColor: APP_UI.card,
          backgroundImage: "none",
          border: `1px solid ${APP_UI.border}`,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          color: APP_UI.text,
          backgroundColor: APP_UI.surfaceRaised,
          border: `1px solid ${APP_UI.border}`,
          "& .MuiChip-label": { color: "inherit" },
          "& .MuiChip-icon": { color: "inherit" },
          "& .MuiChip-deleteIcon": { color: APP_UI.textMuted },
          "&.Mui-disabled": {
            opacity: 1,
            color: "rgba(245,245,247,0.46)",
            backgroundColor: "rgba(24,27,31,0.94)",
            borderColor: "rgba(255,255,255,0.075)",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          minHeight: 40,
          textTransform: "none",
          fontWeight: 800,
          "&.Mui-disabled": {
            opacity: 1,
            color: "rgba(245,245,247,0.43)",
            WebkitTextFillColor: "rgba(245,245,247,0.43)",
          },
        },
        contained: {
          boxShadow: "none",
          "&.Mui-disabled": {
            backgroundColor: "rgba(24,27,31,0.94)",
          },
        },
        outlined: {
          borderColor: APP_UI.accentBorder,
          backgroundColor: APP_UI.card,
          "&:hover": { backgroundColor: APP_UI.cardHover },
          "&.Mui-disabled": {
            borderColor: "rgba(255,255,255,0.12)",
            backgroundColor: "rgba(24,27,31,0.96)",
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: APP_UI.text,
          "&.Mui-disabled": {
            opacity: 1,
            color: "rgba(245,245,247,0.32)",
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          color: APP_UI.text,
          alignItems: "center",
          // MUI v7 typisiert nur die kanonischen Alert-Slots (z. B. root/standard).
          // Severity-spezifische Klassen werden deshalb bewusst innerhalb des
          // gültigen root-Slots gestylt statt über nicht existierende Keys wie
          // standardInfo/standardWarning. Das hält den Dark-Fallback typensicher.
          "&.MuiAlert-standardInfo": {
            backgroundColor: APP_UI.accentSurface,
            border: `1px solid ${APP_UI.accentBorder}`,
            "& .MuiAlert-icon": { color: APP_UI.accent },
          },
          "&.MuiAlert-standardSuccess": {
            backgroundColor: APP_UI.successSurface,
            border: "1px solid rgba(105,210,125,0.30)",
            "& .MuiAlert-icon": { color: APP_UI.success },
          },
          "&.MuiAlert-standardWarning": {
            backgroundColor: APP_UI.warningSurface,
            border: "1px solid rgba(244,163,64,0.32)",
            "& .MuiAlert-icon": { color: APP_UI.warning },
          },
          "&.MuiAlert-standardError": {
            backgroundColor: APP_UI.errorSurface,
            border: "1px solid rgba(255,91,98,0.30)",
            "& .MuiAlert-icon": { color: APP_UI.error },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          color: APP_UI.text,
          backgroundColor: APP_UI.surfaceSolid,
          backgroundImage: "none",
          border: `1px solid ${APP_UI.border}`,
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          color: APP_UI.text,
          backgroundColor: APP_UI.surfaceSolid,
          backgroundImage: "none",
          border: `1px solid ${APP_UI.border}`,
          boxShadow: "0 18px 44px rgba(0,0,0,.58)",
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          color: APP_UI.text,
          backgroundColor: APP_UI.surfaceSolid,
          backgroundImage: "none",
          border: `1px solid ${APP_UI.border}`,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: APP_UI.text,
          "&:hover": { backgroundColor: APP_UI.cardHover },
          "&.Mui-selected": {
            color: APP_UI.accent,
            backgroundColor: APP_UI.accentSoft,
          },
          "&.Mui-selected:hover": { backgroundColor: APP_UI.accentSoft },
          "&.Mui-disabled": {
            opacity: 1,
            color: "rgba(245,245,247,0.40)",
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          color: APP_UI.text,
          backgroundColor: "rgba(12,14,16,0.98)",
          border: `1px solid ${APP_UI.borderStrong}`,
          fontSize: "0.78rem",
        },
        arrow: { color: "rgba(12,14,16,0.98)" },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: APP_UI.text,
          backgroundColor: APP_UI.card,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: APP_UI.borderStrong },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.55)" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: APP_UI.accent, borderWidth: 2 },
          "&.Mui-disabled": {
            color: "rgba(245,245,247,0.46)",
            backgroundColor: "rgba(24,27,31,0.96)",
          },
          "&.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.10)" },
          "&.Mui-disabled input": { WebkitTextFillColor: "rgba(245,245,247,0.46)" },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: "rgba(255,255,255,0.72)",
          "&.Mui-focused": { color: APP_UI.accent },
          "&.Mui-disabled": { color: "rgba(245,245,247,0.40)" },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: APP_UI.textMuted,
          "&.Mui-error": { color: APP_UI.error },
          "&.Mui-disabled": { color: "rgba(245,245,247,0.38)" },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          color: APP_UI.textMuted,
          "&.Mui-checked": { color: APP_UI.accent },
          "&.Mui-checked + .MuiSwitch-track": { backgroundColor: APP_UI.accent, opacity: 0.42 },
          "&.Mui-disabled": { color: "rgba(245,245,247,0.30)" },
          "&.Mui-disabled + .MuiSwitch-track": { backgroundColor: "rgba(255,255,255,0.13)", opacity: 1 },
        },
        track: {
          backgroundColor: "rgba(255,255,255,0.24)",
          opacity: 1,
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: APP_UI.textMuted,
          "&.Mui-checked": { color: APP_UI.accent },
          "&.Mui-disabled": { color: "rgba(245,245,247,0.30)" },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(255,255,255,0.10)",
          borderRadius: 999,
        },
        bar: {
          backgroundColor: APP_UI.accent,
          borderRadius: 999,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          color: APP_UI.text,
          backgroundColor: APP_UI.card,
          backgroundImage: "none",
          border: `1px solid ${APP_UI.border}`,
          boxShadow: "none",
          "&::before": { display: "none" },
          "&.Mui-disabled": {
            opacity: 1,
            color: "rgba(245,245,247,0.43)",
            backgroundColor: "rgba(24,27,31,0.96)",
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          color: APP_UI.text,
          "&.Mui-disabled": { color: "rgba(245,245,247,0.43)" },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: APP_UI.border },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          color: APP_UI.text,
          backgroundColor: "rgba(39,40,43,0.96)",
          border: `1px solid ${APP_UI.border}`,
        },
      },
    },
  },
});

export default theme;
